import { Link } from "react-router-dom";
import { useState } from "react";
import {
  ArrowLeft,
  Download,
  Eye,
  Building2,
  Package,
  Truck,
  Users,
  ShieldCheck,
  Compass,
  Target,
  Award,
  CheckCircle2,
  Phone,
  Mail,
  Globe,
  MapPin,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BrochureContactForm from "@/components/BrochureContactForm";
import BrochurePreview from "@/components/BrochurePreview";
import { generateBrochure } from "@/lib/lazyPdfGenerator";
import { toast } from "sonner";
import logo from "@/assets/arabian-alwaha-logo.jpg";
import heroImg from "@/assets/brochure-industrial-cover.jpg";
import aboutTeam from "@/assets/brochure-operations-team.jpg";

// Client logos
import saipemLogo from "@/assets/clients/saipem-logo.png";
import satorpLogo from "@/assets/clients/satorp-logo.jpeg";
import samsungLogo from "@/assets/clients/samsung-logo.jpeg";
import hyundaiLogo from "@/assets/clients/hyundai-logo.jpeg";
import aytbLogo from "@/assets/clients/aytb-logo.jpeg";
import kanooLogo from "@/assets/clients/kanoo-logo.jpeg";
import skLogo from "@/assets/clients/sk-logo.jpeg";
import maadenLogo from "@/assets/clients/maaden-logo.jpeg";
import kbrLogo from "@/assets/clients/kbr-logo.jpeg";
import discoveryLogo from "@/assets/clients/discovery-logo.png";
import carloGavazziLogo from "@/assets/clients/carlo-gavazzi-logo.png";
import seapamLogo from "@/assets/clients/seapam-logo.jpeg";
import ltLogo from "@/assets/clients/lt-logo.jpeg";
import kccLogo from "@/assets/clients/kcc-logo.png";
import jgcLogo from "@/assets/clients/jgc-logo.png";

const Brochure = () => {
  const { language } = useLanguage();
  const isArabic = language === "ar";
  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await generateBrochure(language);
      toast.success(isArabic ? "تم تنزيل الكتيب بنجاح" : "Brochure downloaded successfully");
    } catch {
      toast.error(isArabic ? "فشل تنزيل الكتيب" : "Failed to download brochure");
    } finally {
      setDownloading(false);
    }
  };

  const t = {
    nav: {
      back: isArabic ? "العودة إلى الموقع" : "Back to website",
      download: isArabic ? "تحميل PDF" : "Download PDF",
      downloading: isArabic ? "جاري التحضير..." : "Preparing...",
      preview: isArabic ? "معاينة" : "Preview",
    },
    cover: {
      eyebrow: isArabic ? "كتيب الشركة · ٢٠٢٤" : "Company Brochure · 2024",
      title: isArabic
        ? "هندسة الثقة في صناعة النفط والغاز"
        : "Engineering trust across oil, gas & industry",
      lede: isArabic
        ? "منذ عام ٢٠٠٨، تقدم مؤسسة العربية الواحة حلولاً متكاملة للمقاولات والتجارة في المملكة العربية السعودية ودول الخليج، بمعايير من الطراز العالمي."
        : "Since 2008, Arabian Alwaha Establishment has delivered integrated contracting and trading solutions across the Kingdom of Saudi Arabia and the GCC, to world-class standards.",
      stats: [
        { value: "15+", label: isArabic ? "سنة من الخبرة" : "Years of expertise" },
        { value: "75+", label: isArabic ? "مشروع منجز" : "Projects delivered" },
        { value: "15+", label: isArabic ? "عميل رئيسي" : "Major clients" },
        { value: "24/7", label: isArabic ? "دعم ميداني" : "Field support" },
      ],
    },
    overview: {
      eyebrow: isArabic ? "نبذة عن الشركة" : "Company overview",
      title: isArabic
        ? "شريكك الموثوق في الجبيل والمنطقة الشرقية"
        : "A trusted partner in Jubail and the Eastern Province",
      body1: isArabic
        ? "تأسست العربية الواحة (المعروفة سابقاً باسم Gulfcoast Contracting) لتكون امتداداً لخبرة طويلة في خدمة قطاعات الطاقة والصناعة والبنية التحتية. نعمل من قلب الجبيل الصناعية، ونخدم كبرى الشركات العاملة في النفط والغاز والبتروكيماويات والتعدين."
        : "Arabian Alwaha (formerly Gulfcoast Contracting) was founded as the natural evolution of decades of expertise serving energy, industrial and infrastructure sectors. We operate from the heart of Jubail Industrial City, supporting the most demanding owners and EPC contractors in oil & gas, petrochemicals, and mining.",
      body2: isArabic
        ? "نؤمن بأن التميز ليس حدثاً عابراً بل نتيجة تراكم الانضباط والخبرة والاهتمام بأدق التفاصيل. هذا ما نقدمه في كل مشروع — صغيراً كان أو كبيراً."
        : "We believe excellence is not a moment — it is the compounded result of discipline, deep expertise, and obsessive attention to detail. That is what we bring to every project, large or small.",
      pillars: [
        {
          icon: Compass,
          title: isArabic ? "رؤيتنا" : "Our vision",
          body: isArabic
            ? "أن نكون الشريك المفضل لقطاع الطاقة والصناعة في المملكة من خلال حلول تجمع بين الانضباط الهندسي والخدمة الإنسانية."
            : "To be the preferred partner for the Kingdom's energy and industrial sectors — combining engineering discipline with human-centered service.",
        },
        {
          icon: Target,
          title: isArabic ? "رسالتنا" : "Our mission",
          body: isArabic
            ? "تقديم حلول مقاولات وتجارة موثوقة، تحقق قيمة طويلة الأمد، وتنفذ بأعلى معايير السلامة والجودة في كل مرة."
            : "Deliver dependable contracting and trading solutions that create long-term value and are executed to the highest safety and quality standards — every time.",
        },
        {
          icon: ShieldCheck,
          title: isArabic ? "قيمنا" : "Our values",
          body: isArabic
            ? "النزاهة، السلامة أولاً، إتقان التنفيذ، احترام الالتزامات، والشراكة الحقيقية مع كل عميل وكل فرد في فريقنا."
            : "Integrity, safety first, mastery of execution, honoring commitments, and genuine partnership — with every client and every member of our team.",
        },
      ],
    },
    services: {
      eyebrow: isArabic ? "خدماتنا" : "Capabilities",
      title: isArabic
        ? "أربعة محاور خدمة، منظومة عمل واحدة"
        : "Four service pillars. One integrated operation.",
      lede: isArabic
        ? "من المقاولات العامة إلى توريد المعدات والمواد، كل خدمة مصممة لتعمل بانسجام مع الأخرى — لتقليل التعقيد وتسريع التنفيذ."
        : "From general contracting to materials, equipment and manpower — each service is designed to interlock with the others, reducing complexity and accelerating delivery.",
      items: [
        {
          icon: Building2,
          title: isArabic ? "المقاولات العامة" : "General Contracting",
          desc: isArabic
            ? "تنفيذ مشاريع متكاملة في قطاعات النفط والغاز والصناعة، من التخطيط حتى التسليم."
            : "End-to-end project execution across oil, gas and industrial sectors — from planning through commissioning.",
          features: isArabic
            ? ["تنفيذ المشاريع", "دعم البناء", "أعمال الصيانة", "ضمان الجودة"]
            : ["Project execution", "Construction support", "Maintenance works", "Quality assurance"],
        },
        {
          icon: Package,
          title: isArabic ? "تجارة المواد" : "Materials Trading",
          desc: isArabic
            ? "توريد مواد البناء والمستلزمات الصناعية من شركاء عالميين موثوقين، بأسعار تنافسية ومواعيد دقيقة."
            : "Premium construction materials and industrial supplies from trusted global partners — competitively priced, precisely scheduled.",
          features: isArabic
            ? ["توريد المواد", "جودة معتمدة", "تسليم في الوقت", "أسعار تنافسية"]
            : ["Material supply", "Certified quality", "On-time delivery", "Competitive pricing"],
        },
        {
          icon: Truck,
          title: isArabic ? "تأجير المعدات" : "Equipment Rental",
          desc: isArabic
            ? "أسطول حديث ومتكامل من المعدات والآليات للمشاريع الإنشائية والصناعية، بشروط مرنة."
            : "A modern, well-maintained fleet of construction and industrial equipment — flexible terms, rapid mobilization.",
          features: isArabic
            ? ["أسطول واسع", "معدات حديثة", "تسليم سريع", "شروط مرنة"]
            : ["Extensive fleet", "Modern equipment", "Rapid delivery", "Flexible terms"],
        },
        {
          icon: Users,
          title: isArabic ? "حلول القوى العاملة" : "Workforce Solutions",
          desc: isArabic
            ? "فرق فنية ومهنية مؤهلة، مدربة على أعلى معايير السلامة، جاهزة للتعبئة في الوقت الذي يناسب مشروعك."
            : "Skilled, safety-trained technical and professional teams — ready to mobilize on your project's timeline.",
          features: isArabic
            ? ["كوادر متخصصة", "عمالة ماهرة", "دعم احترافي", "خدمة موثوقة"]
            : ["Expert personnel", "Skilled workforce", "Professional support", "Reliable service"],
        },
      ],
    },
    proof: {
      eyebrow: isArabic ? "الثقة المكتسبة" : "Earned trust",
      title: isArabic
        ? "شراكات مع كبرى الشركات العالمية والإقليمية"
        : "Partnered with the world's most demanding operators",
      lede: isArabic
        ? "تتحدث قائمة عملائنا عن مستوى الانضباط الذي نلتزم به. كل شعار هنا يمثل علاقة مبنية على الثقة والأداء المتسق."
        : "Our client roster speaks to the discipline we hold ourselves to. Each logo represents a relationship built on trust and consistent performance.",
    },
    projects: {
      eyebrow: isArabic ? "مشاريع مختارة" : "Selected projects",
      title: isArabic ? "نماذج من سجل أعمالنا" : "A glimpse of our delivery record",
    },
    cta: {
      title: isArabic ? "هل أنت مستعد لبدء مشروعك القادم؟" : "Ready to start your next project?",
      body: isArabic
        ? "تواصل مع فريقنا اليوم لمناقشة متطلباتك. نستجيب خلال ساعات."
        : "Talk to our team about your requirements. We respond within hours.",
      primary: isArabic ? "اطلب عرض سعر" : "Request a quote",
      secondary: isArabic ? "تواصل معنا" : "Contact us",
    },
    footer: {
      tagline: isArabic
        ? "مؤسسة العربية الواحة للمقاولات والتجارة"
        : "Arabian Alwaha Contracting & Trading Establishment",
      address: isArabic
        ? "الجبيل الصناعية، المنطقة الشرقية، المملكة العربية السعودية"
        : "Jubail Industrial City, Eastern Province, Saudi Arabia",
      rights: isArabic ? "© ٢٠٢٤ جميع الحقوق محفوظة" : "© 2024 All rights reserved",
    },
  };

  const clients = [
    { name: "Saipem", logo: saipemLogo },
    { name: "SATORP", logo: satorpLogo },
    { name: "AYTB", logo: aytbLogo },
    { name: "Kanoo", logo: kanooLogo },
    { name: "Carlo Gavazzi", logo: carloGavazziLogo },
    { name: "SK E&C", logo: skLogo },
    { name: "JGC", logo: jgcLogo },
    { name: "Discovery", logo: discoveryLogo },
    { name: "Ma'aden", logo: maadenLogo },
    { name: "Samsung", logo: samsungLogo },
    { name: "Hyundai", logo: hyundaiLogo },
    { name: "KBR", logo: kbrLogo },
    { name: "L&T", logo: ltLogo },
    { name: "KCC", logo: kccLogo },
    { name: "Seapam", logo: seapamLogo },
  ];

  const projects = [
    {
      name: "SATORP Refinery",
      client: "Seapam",
      period: "2010 – 2013",
      type: isArabic ? "البنية التحتية للنفط والغاز" : "Oil & Gas Infrastructure",
    },
    {
      name: "Dammam MENA Port",
      client: "Saipem",
      period: "2013 – 2014",
      type: isArabic ? "تطوير الموانئ واللوجستيات" : "Port Development & Logistics",
    },
    {
      name: "Manifa Field",
      client: "Discovery",
      period: "2012 – 2015",
      type: isArabic ? "تطوير حقل بحري" : "Offshore Field Development",
    },
    {
      name: "Ma'aden Phosphate",
      client: "Samsung",
      period: "2011 – 2014",
      type: isArabic ? "معالجة المعادن" : "Mining & Minerals Processing",
    },
    {
      name: "SATORP I&C",
      client: "Carlo Gavazzi",
      period: "2010 – 2013",
      type: isArabic ? "أنظمة التحكم والقياس" : "Instrumentation & Control",
    },
    {
      name: "Industrial Support",
      client: "JGC / KBR",
      period: "2014 – Present",
      type: isArabic ? "خدمات تشغيل وصيانة" : "Operations & Maintenance",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sticky chrome */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border/40">
        <div className="container mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.nav.back}
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPreviewOpen(true)}
            >
              <Eye className="w-4 h-4 mr-2" />
              {t.nav.preview}
            </Button>
            <Button
              size="sm"
              onClick={handleDownload}
              disabled={downloading}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t.nav.downloading}
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  {t.nav.download}
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* COVER */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImg})` }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, hsl(160 65% 12% / 0.96) 0%, hsl(160 55% 22% / 0.92) 55%, hsl(45 80% 30% / 0.85) 100%)",
          }}
          aria-hidden="true"
        />
        <div className="relative container mx-auto px-6 lg:px-10 py-24 lg:py-36">
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 mb-10">
              <div className="w-14 h-14 rounded-xl bg-white/95 p-1.5 shadow-elegant">
                <img src={logo} alt="Arabian Alwaha" className="w-full h-full object-contain" />
              </div>
              <div className="text-white/85">
                <p className="text-xs uppercase tracking-[0.25em] font-semibold text-secondary">
                  Arabian Alwaha
                </p>
                <p className="text-sm">Contracting & Trading Est.</p>
              </div>
            </div>

            <p className="text-secondary text-xs uppercase tracking-[0.3em] font-bold mb-6">
              {t.cover.eyebrow}
            </p>
            <h1 className="text-white font-bold tracking-tight text-4xl md:text-6xl lg:text-7xl leading-[1.05] mb-8">
              {t.cover.title}
            </h1>
            <p className="text-white/85 text-lg lg:text-xl max-w-2xl leading-relaxed">
              {t.cover.lede}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-px mt-16 bg-white/15 border border-white/15 rounded-xl overflow-hidden">
              {t.cover.stats.map((s) => (
                <div key={s.label} className="bg-white/[0.04] backdrop-blur-sm px-6 py-7">
                  <p className="text-secondary text-3xl md:text-4xl font-bold tracking-tight mb-1">
                    {s.value}
                  </p>
                  <p className="text-white/75 text-xs md:text-sm font-medium leading-snug">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-24 lg:py-32 bg-background">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <p className="text-primary text-xs uppercase tracking-[0.3em] font-bold mb-5">
                {t.overview.eyebrow}
              </p>
              <h2 className="text-4xl lg:text-5xl font-bold text-foreground tracking-tight leading-[1.1] mb-8">
                {t.overview.title}
              </h2>
              <div className="rounded-2xl overflow-hidden shadow-elegant">
                <img
                  src={aboutTeam}
                  alt="Arabian Alwaha team"
                  className="w-full h-80 object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6">
              <p className="text-lg text-muted-foreground leading-relaxed">{t.overview.body1}</p>
              <p className="text-lg text-muted-foreground leading-relaxed">{t.overview.body2}</p>

              <div className="grid gap-4 mt-10">
                {t.overview.pillars.map((p) => (
                  <div
                    key={p.title}
                    className="group flex gap-5 p-6 rounded-2xl border border-border/60 bg-card/50 hover:border-primary/40 hover:shadow-soft transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <p.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground mb-1.5">{p.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 lg:py-32 bg-muted/30 border-y border-border/50">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <p className="text-primary text-xs uppercase tracking-[0.3em] font-bold mb-5">
              {t.services.eyebrow}
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground tracking-tight leading-[1.1] mb-6">
              {t.services.title}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">{t.services.lede}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {t.services.items.map((s, i) => (
              <article
                key={s.title}
                className="group relative bg-card border border-border/60 rounded-3xl p-8 lg:p-10 hover:shadow-elegant hover:border-primary/30 transition-all overflow-hidden"
              >
                <span className="absolute top-6 right-6 text-xs font-mono text-muted-foreground/60 tracking-widest">
                  {String(i + 1).padStart(2, "0")} / 04
                </span>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground flex items-center justify-center mb-7 shadow-soft">
                  <s.icon className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-3 tracking-tight">
                  {s.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-7">{s.desc}</p>
                <ul className="space-y-2.5 pt-6 border-t border-border/50">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-foreground/80">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      <section className="py-24 lg:py-32 bg-background">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <p className="text-primary text-xs uppercase tracking-[0.3em] font-bold mb-5">
              {t.proof.eyebrow}
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground tracking-tight leading-[1.1] mb-6">
              {t.proof.title}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">{t.proof.lede}</p>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-5 gap-px bg-border/60 border border-border/60 rounded-2xl overflow-hidden">
            {clients.map((c) => (
              <div
                key={c.name}
                className="bg-card aspect-[4/3] flex flex-col items-center justify-center p-5 hover:bg-primary/[0.04] transition-colors"
              >
                <img
                  src={c.logo}
                  alt={`${c.name} logo`}
                  className="max-h-14 max-w-full object-contain grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                  loading="lazy"
                />
                <p className="text-[11px] font-semibold text-muted-foreground mt-3 tracking-wide">
                  {c.name}
                </p>
              </div>
            ))}
          </div>

          {/* Projects table */}
          <div className="mt-20">
            <div className="flex items-baseline gap-3 mb-8">
              <Award className="w-5 h-5 text-primary" />
              <p className="text-primary text-xs uppercase tracking-[0.3em] font-bold">
                {t.projects.eyebrow}
              </p>
            </div>
            <h3 className="text-3xl lg:text-4xl font-bold text-foreground tracking-tight mb-10">
              {t.projects.title}
            </h3>

            <div className="border border-border/60 rounded-2xl overflow-hidden divide-y divide-border/60">
              {projects.map((p) => (
                <div
                  key={p.name + p.client}
                  className="grid grid-cols-12 gap-4 px-6 py-5 items-center hover:bg-muted/40 transition-colors"
                >
                  <div className="col-span-12 md:col-span-5">
                    <p className="font-semibold text-foreground">{p.name}</p>
                    <p className="text-sm text-muted-foreground md:hidden mt-1">
                      {p.client} · {p.period}
                    </p>
                  </div>
                  <p className="hidden md:block md:col-span-3 text-sm font-medium text-primary">
                    {p.client}
                  </p>
                  <p className="hidden md:block md:col-span-2 text-sm font-mono text-muted-foreground">
                    {p.period}
                  </p>
                  <p className="col-span-12 md:col-span-2 text-sm text-muted-foreground md:text-right">
                    {p.type}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-28 overflow-hidden bg-primary text-primary-foreground">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, hsl(var(--primary-foreground)) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
          aria-hidden="true"
        />
        <div className="relative container mx-auto px-6 lg:px-10 text-center max-w-3xl">
          <h2 className="text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-6">
            {t.cta.title}
          </h2>
          <p className="text-lg text-primary-foreground/85 mb-10">{t.cta.body}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              asChild
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold px-8"
            >
              <Link to="/#quote">{t.cta.primary}</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground hover:text-primary font-semibold px-8"
            >
              <Link to="/#contact">{t.cta.secondary}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section
        id="brochure-contact"
        className="bg-muted/30 border-t border-border/60 py-20"
      >
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-[0.18em] text-primary uppercase mb-3">
              {isArabic ? "تواصل معنا" : "Get in touch"}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
              {isArabic ? "أرسل استفسارك مباشرة إلى فريق المبيعات" : "Send your enquiry directly to our sales team"}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {isArabic
                ? "نرد على جميع الاستفسارات خلال ساعات على sales@arabianalwaha.com."
                : "We respond to every enquiry within hours via sales@arabianalwaha.com."}
            </p>
          </div>
          <BrochureContactForm isArabic={isArabic} />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-background border-t border-border/60 py-14">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-10 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-lg bg-white p-1 border border-border">
                  <img src={logo} alt="Arabian Alwaha" className="w-full h-full object-contain" />
                </div>
                <p className="font-bold text-foreground">Arabian Alwaha</p>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{t.footer.tagline}</p>
            </div>
            <div className="space-y-3 text-sm">
              <p className="flex items-center gap-3 text-muted-foreground">
                <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                {t.footer.address}
              </p>
              <p className="flex items-center gap-3 text-muted-foreground">
                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                <a href="tel:+966504838870" className="hover:text-primary transition-colors">
                  <bdi dir="ltr" className="font-mono">+966 50 483 8870</bdi>
                </a>
              </p>
              <p className="flex items-center gap-3 text-muted-foreground">
                <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                <a
                  href="mailto:sales@arabianalwaha.com"
                  className="hover:text-primary transition-colors"
                >
                  sales@arabianalwaha.com
                </a>
              </p>
              <p className="flex items-center gap-3 text-muted-foreground">
                <Globe className="w-4 h-4 text-primary flex-shrink-0" />
                <a
                  href="https://www.arabianalwaha.com"
                  className="hover:text-primary transition-colors"
                >
                  www.arabianalwaha.com
                </a>
              </p>
            </div>
            <div className="md:text-right">
              <Button
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                className="mr-2"
              >
                <Eye className="w-4 h-4 mr-2" />
                {t.nav.preview}
              </Button>
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="bg-primary hover:bg-primary/90"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {downloading ? t.nav.downloading : t.nav.download}
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center pt-8 border-t border-border/40">
            {t.footer.rights}
          </p>
        </div>
      </footer>
      <BrochurePreview open={previewOpen} onOpenChange={setPreviewOpen} />
    </div>
  );
};

export default Brochure;